const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR;
const MINUTES_PER_DAY = MINUTES_PER_HOUR * HOURS_PER_DAY;

/**
 * Einzige Dauer-Formatierung des Frontends. Waehlt die Einheit nach der
 * Groessenordnung, damit lange Laufzeiten nicht als fuenfstellige Sekundenzahl
 * erscheinen und die schmale Dauer-Spalte sprengen (NEX-1130).
 *
 * <p>Unterhalb einer Minute bleibt eine Dezimale erhalten, weil automatisierte
 * Laeufe sich genau dort unterscheiden; eine bedeutungslose `.0` faellt weg.
 * Eine positive Dauer unter 0.1 s wird als Schranke statt als „0 s" gezeigt,
 * sonst liesse sie sich als „keine Dauer" missverstehen.
 */
export function formatDurationMs(durationMs: number | null | undefined): string {
  if (durationMs === null || durationMs === undefined || durationMs < 0) return '—';
  if (durationMs === 0) return '0 s';

  const seconds = Math.round(durationMs / 100) / 10;
  if (seconds < 0.1) return '<0.1 s';
  if (seconds < SECONDS_PER_MINUTE) return `${seconds} s`;

  // Ab hier zaehlt die Sekunden-Dezimale nicht mehr; jede Stufe rundet auf ihre
  // kleinste angezeigte Einheit, damit 59.999 s als „1 min" und nicht als
  // „0 min 60 s" erscheint.
  const totalSeconds = Math.round(durationMs / MS_PER_SECOND);
  if (totalSeconds < SECONDS_PER_HOUR) {
    return join(Math.floor(totalSeconds / SECONDS_PER_MINUTE), 'min', totalSeconds % SECONDS_PER_MINUTE, 's');
  }

  const totalMinutes = Math.round(totalSeconds / SECONDS_PER_MINUTE);
  if (totalMinutes < MINUTES_PER_DAY) {
    return join(Math.floor(totalMinutes / MINUTES_PER_HOUR), 'h', totalMinutes % MINUTES_PER_HOUR, 'min');
  }

  const totalHours = Math.round(totalMinutes / MINUTES_PER_HOUR);
  return join(Math.floor(totalHours / HOURS_PER_DAY), 'd', totalHours % HOURS_PER_DAY, 'h');
}

/** Wie {@link formatDurationMs}, fuer Endpunkte die ihre Dauer in Sekunden liefern. */
export function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  return formatDurationMs(seconds * MS_PER_SECOND);
}

/**
 * Dauer zwischen zwei ISO-Zeitstempeln. Liefert bewusst einen leeren String
 * statt eines Gedankenstrichs, wenn die Spanne unbekannt ist: die Aufrufer
 * zeigen die Zeile dann gar nicht an.
 */
export function formatDurationBetween(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt || !completedAt) return '';
  const start = Date.parse(startedAt);
  const end = Date.parse(completedAt);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return '';
  return formatDurationMs(end - start);
}

function join(major: number, majorUnit: string, minor: number, minorUnit: string): string {
  return minor === 0 ? `${major} ${majorUnit}` : `${major} ${majorUnit} ${minor} ${minorUnit}`;
}

/**
 * Mitlaufende Uhr eines Durchlaufs als {@code M:SS}. Bewusst nicht
 * {@link formatDurationMs}: die Uhr tickt im Sekundentakt und duerfte dabei ihre
 * Einheit nicht wechseln — die Kopfzeile spraenge sonst bei jeder Minute um. Die
 * Minuten laufen aus demselben Grund ueber die Stunde hinaus weiter.
 */
export function formatElapsedClock(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / MS_PER_SECOND));
  const seconds = String(totalSeconds % SECONDS_PER_MINUTE).padStart(2, '0');
  return `${Math.floor(totalSeconds / SECONDS_PER_MINUTE)}:${seconds}`;
}

/**
 * Dauer eines automatisierten Auftrags als „4 m 12 s“, ab einer Stunde „1 h 4 m 12 s“ (NEX-1654).
 * Bewusst nicht {@link formatDurationMs}: Die Karte zeigt immer Minuten und Sekunden, auch wenn
 * die Dauer mitlaeuft, statt die Einheit nach der Groessenordnung zu wechseln.
 */
export function formatJobDuration(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / MS_PER_SECOND));
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return hours > 0 ? `${hours} h ${minutes} m ${seconds} s` : `${minutes} m ${seconds} s`;
}
